<template>
  <div class="controle-creances-manager">
    <!-- Header -->
    <div class="section-header">
      <div class="header-info">
        <h3><i class="fas fa-clipboard-check"></i> Contrôle des Créances</h3>
        <p>Validation des missions avec paiement partiel</p>
      </div>
      <div class="header-stats">
        <div class="stat-badge warning">
          <i class="fas fa-clock"></i>
          <span>{{ creancesEnAttente.length }} en attente</span>
        </div>
        <div class="stat-badge info">
          <span class="montant">{{ formatCurrency(totalEnAttente) }}</span>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement des créances...</p>
    </div>

    <!-- Liste vide -->
    <div v-else-if="creancesEnAttente.length === 0" class="empty-state">
      <div class="empty-icon"><i class="fas fa-check-circle"></i></div>
      <h4>Aucune créance en attente</h4>
      <p>Toutes les créances ont été contrôlées</p>
    </div>

    <!-- Liste des créances -->
    <div v-else class="creances-grid">
      <div 
        v-for="creance in creancesEnAttente" 
        :key="creance._id"
        class="creance-card"
      >
        <div class="card-header">
          <div class="client-info">
            <h4>{{ creance.client?.nomComplet || 'Client' }}</h4>
            <p class="telephone">
              <i class="fas fa-phone"></i>
              {{ creance.client?.telephone }}
            </p>
            <p class="date">
              <i class="fas fa-calendar"></i>
              {{ formatDate(creance.dateIntervention) }}
            </p>
          </div>
          <div class="montants-resume">
            <div class="montant-item prix">
              <span class="label">Prix mission</span>
              <span class="value">{{ formatCurrency(creance.prixMission) }}</span>
            </div>
          </div>
        </div>

        <div class="card-body">
          <div class="paiement-details">
            <div class="detail-row">
              <span class="label"><i class="fas fa-money-bill-wave"></i> Payé par client</span>
              <span class="value success">{{ formatCurrency(creance.montantPaye) }}</span>
            </div>
            <div class="detail-row creance-row">
              <span class="label"><i class="fas fa-exclamation-triangle"></i> Créance</span>
              <span class="value danger">{{ formatCurrency(creance.montantCreance) }}</span>
            </div>
          </div>

          <div class="intervention-info">
            <div class="info-item">
              <i class="fas fa-tools"></i>
              <span>{{ creance.typeIntervention || 'Installation' }}</span>
            </div>
            <div class="info-item">
              <i class="fas fa-user-cog"></i>
              <span>{{ creance.technicien?.prenom }} {{ creance.technicien?.nom }}</span>
            </div>
          </div>
        </div>

        <div class="card-actions">
          <button @click="openValidationModal(creance)" class="btn btn-success">
            <i class="fas fa-check"></i>
            Valider → Recouvrement
          </button>
          <button @click="openRejetModal(creance)" class="btn btn-danger">
            <i class="fas fa-times"></i>
            Rejeter
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL VALIDATION CRÉANCE -->
    <div v-if="showValidationModal" class="modal-overlay" @click.self="closeValidationModal">
      <div class="validation-modal">
        <div class="modal-icon success">
          <i class="fas fa-clipboard-check"></i>
        </div>
        <h4 class="modal-title">Valider cette créance ?</h4>
        <p class="modal-subtitle">La créance sera transférée en recouvrement</p>
        
        <div class="modal-details">
          <div class="detail-card">
            <div class="detail-header">
              <i class="fas fa-user"></i>
              <span>{{ selectedCreance?.client?.nomComplet }}</span>
            </div>
            <div class="detail-body">
              <div class="detail-item">
                <span class="label">Prix mission</span>
                <span class="value">{{ formatCurrency(selectedCreance?.prixMission) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Montant payé</span>
                <span class="value success">{{ formatCurrency(selectedCreance?.montantPaye) }}</span>
              </div>
              <div class="detail-item highlight">
                <span class="label">Créance à recouvrer</span>
                <span class="value danger">{{ formatCurrency(selectedCreance?.montantCreance) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button @click="closeValidationModal" class="btn-modal cancel">
            <i class="fas fa-times"></i>
            Annuler
          </button>
          <button @click="confirmValidation" class="btn-modal confirm">
            <i class="fas fa-check"></i>
            Confirmer la validation
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL REJET CRÉANCE -->
    <div v-if="showRejetModal" class="modal-overlay" @click.self="closeRejetModal">
      <div class="validation-modal rejet">
        <div class="modal-icon danger">
          <i class="fas fa-ban"></i>
        </div>
        <h4 class="modal-title">Rejeter cette créance ?</h4>
        <p class="modal-subtitle">Veuillez indiquer le motif du rejet</p>
        
        <div class="modal-details">
          <div class="detail-card">
            <div class="detail-header">
              <i class="fas fa-user"></i>
              <span>{{ selectedCreance?.client?.nomComplet }}</span>
            </div>
            <div class="detail-body">
              <div class="detail-item highlight danger-bg">
                <span class="label">Créance</span>
                <span class="value">{{ formatCurrency(selectedCreance?.montantCreance) }}</span>
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label><i class="fas fa-comment-alt"></i> Motif du rejet *</label>
            <textarea 
              v-model="rejetMotif"
              placeholder="Expliquez la raison du rejet..."
              rows="3"
              class="form-textarea"
            ></textarea>
          </div>
        </div>

        <div class="modal-actions">
          <button @click="closeRejetModal" class="btn-modal cancel">
            <i class="fas fa-arrow-left"></i>
            Annuler
          </button>
          <button 
            @click="confirmRejet" 
            :disabled="!rejetMotif.trim()"
            class="btn-modal reject"
          >
            <i class="fas fa-times"></i>
            Confirmer le rejet
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import api from '@/services/api'

export default {
  name: 'ControleCreancesManager',
  props: {
    userRole: String
  },
  setup() {
    const toast = useToast()
    const loading = ref(false)
    const creancesEnAttente = ref([])
    
    // Modals
    const showValidationModal = ref(false)
    const showRejetModal = ref(false)
    const selectedCreance = ref(null)
    const rejetMotif = ref('')

    const totalEnAttente = computed(() => {
      return creancesEnAttente.value.reduce((sum, c) => sum + c.montantCreance, 0)
    })

    const loadCreances = async () => {
      loading.value = true
      try {
        const response = await api.get('/creances/controle')
        creancesEnAttente.value = response.data.creances || []
      } catch (error) {
        console.error('Erreur chargement créances:', error)
        toast.error('Erreur lors du chargement')
      } finally {
        loading.value = false
      }
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      })
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    // Modal Validation
    const openValidationModal = (creance) => {
      selectedCreance.value = creance
      showValidationModal.value = true
    }

    const closeValidationModal = () => {
      showValidationModal.value = false
      selectedCreance.value = null
    }

    const confirmValidation = async () => {
      if (!selectedCreance.value) return
      
      try {
        await api.put(`/creances/${selectedCreance.value._id}/valider`, { action: 'valider' })
        toast.success('✅ Créance validée et transférée en recouvrement')
        closeValidationModal()
        loadCreances()
      } catch (error) {
        toast.error('Erreur lors de la validation')
      }
    }

    // Modal Rejet
    const openRejetModal = (creance) => {
      selectedCreance.value = creance
      rejetMotif.value = ''
      showRejetModal.value = true
    }

    const closeRejetModal = () => {
      showRejetModal.value = false
      selectedCreance.value = null
      rejetMotif.value = ''
    }

    const confirmRejet = async () => {
      if (!selectedCreance.value || !rejetMotif.value.trim()) return
      
      try {
        await api.put(`/creances/${selectedCreance.value._id}/valider`, { 
          action: 'rejeter', 
          commentaire: rejetMotif.value 
        })
        toast.success('Créance rejetée')
        closeRejetModal()
        loadCreances()
      } catch (error) {
        toast.error('Erreur lors du rejet')
      }
    }

    onMounted(loadCreances)

    return {
      loading, creancesEnAttente, totalEnAttente,
      formatDate, formatCurrency,
      showValidationModal, showRejetModal, selectedCreance, rejetMotif,
      openValidationModal, closeValidationModal, confirmValidation,
      openRejetModal, closeRejetModal, confirmRejet
    }
  }
}
</script>

<style scoped>
.controle-creances-manager { padding: 1rem; }

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-info h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #f59e0b;
  font-size: 1.25rem;
}

.header-info p {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.85rem;
}

.header-stats {
  display: flex;
  gap: 0.75rem;
}

.stat-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.stat-badge.warning {
  background: #fef3c7;
  color: #d97706;
}

.stat-badge.info {
  background: #dbeafe;
  color: #2563eb;
}

.stat-badge .montant {
  font-size: 1rem;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 3rem;
  color: #64748b;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #f59e0b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin { to { transform: rotate(360deg); } }

.empty-icon {
  font-size: 3rem;
  color: #10b981;
  margin-bottom: 1rem;
}

.creances-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 1rem;
}

.creance-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s;
  border-left: 4px solid #f59e0b;
}

.creance-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
}

.client-info h4 {
  margin: 0;
  font-size: 1rem;
  color: #1e293b;
}

.client-info p {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.montants-resume {
  text-align: right;
}

.montant-item {
  display: flex;
  flex-direction: column;
}

.montant-item .label {
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
}

.montant-item .value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
}

.card-body {
  padding: 1rem;
}

.paiement-details {
  background: #f8fafc;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.detail-row:not(:last-child) {
  border-bottom: 1px dashed #e2e8f0;
}

.detail-row .label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #64748b;
}

.detail-row .value {
  font-weight: 600;
  font-size: 0.95rem;
}

.detail-row .value.success { color: #10b981; }
.detail-row .value.danger { color: #ef4444; }

.creance-row {
  background: #fef2f2;
  margin: 0.5rem -0.75rem -0.75rem;
  padding: 0.75rem !important;
  border-radius: 0 0 8px 8px;
}

.intervention-info {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.btn {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-success:hover {
  background: linear-gradient(135deg, #059669, #047857);
}

.btn-danger {
  background: #fee2e2;
  color: #dc2626;
}

.btn-danger:hover {
  background: #fecaca;
}

/* Responsive */
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-stats {
    width: 100%;
    justify-content: space-between;
  }
  
  .creances-grid {
    grid-template-columns: 1fr;
  }
  
  .card-header {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .montants-resume {
    text-align: left;
  }
  
  .card-actions {
    flex-direction: column;
  }
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

/* Modal Validation */
.validation-modal {
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 450px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-icon.success {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
}

.modal-icon.success i {
  font-size: 2.5rem;
  color: #10b981;
}

.modal-icon.danger {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
}

.modal-icon.danger i {
  font-size: 2.5rem;
  color: #ef4444;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem;
}

.modal-subtitle {
  font-size: 0.95rem;
  color: #64748b;
  margin: 0 0 1.5rem;
}

.modal-details {
  margin-bottom: 1.5rem;
}

.detail-card {
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  text-align: left;
}

.detail-header {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  padding: 0.875rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1e293b;
}

.detail-header i {
  color: #f59e0b;
}

.detail-body {
  padding: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.detail-item:not(:last-child) {
  border-bottom: 1px dashed #e2e8f0;
}

.detail-item .label {
  font-size: 0.85rem;
  color: #64748b;
}

.detail-item .value {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1e293b;
}

.detail-item .value.success {
  color: #10b981;
}

.detail-item .value.danger {
  color: #ef4444;
}

.detail-item.highlight {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  margin: 0.5rem -1rem -1rem;
  padding: 0.875rem 1rem;
  border-radius: 0 0 12px 12px;
}

.detail-item.highlight.danger-bg {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
}

.form-group {
  margin-top: 1rem;
  text-align: left;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.form-textarea {
  width: 100%;
  padding: 0.875rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  resize: none;
  transition: all 0.2s;
}

.form-textarea:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn-modal {
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-modal.cancel {
  background: #f1f5f9;
  color: #64748b;
}

.btn-modal.cancel:hover {
  background: #e2e8f0;
  color: #475569;
}

.btn-modal.confirm {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-modal.confirm:hover {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.btn-modal.reject {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.btn-modal.reject:hover:not(:disabled) {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.btn-modal.reject:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .validation-modal {
    padding: 1.5rem;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .btn-modal {
    width: 100%;
    justify-content: center;
  }
}
</style>
